import * as t from 'io-ts';
import moment from 'moment';
import { Action, createActions } from 'redux-actions';

import { ENDPOINT_VERSION, ENDPOINTS_ALPHAS, ENDPOINTS_USER } from '../config';
import { localeData } from '../locales/data';
import {
  AlphasDataResults,
  AlphasListingData,
  AlphasOptions,
  AlphaColumn,
  ChartData,
  CorrelationProd,
  CorrelationSelf,
  RecordsSets,
  CorrelationSelfType,
} from '../reducers/AlphasReducer/AlphasReducer.types';
import { CheckSubmissionValidation } from '../reducers/SimulationReducer/DataReducer/DataReducer.types';
import { FAQ_URL, SUPPORT_URL_PLACEHOLDER } from '../shared/constants';
import { AlphaRangeFilters, MillionFilters, PercentAndPermyriadFilters } from '../shared/data';
import { allColumnsParents } from '../shared/data/initialColumnsTable';
import {
  defaultErrorMessage,
  globalFetch,
  ioTsValidationError,
  isAction,
  specialCasesCorrelation,
  webSimApiErrorFlashMessages,
} from '../shared/helpers/fetch-helper';
import { logErrorService } from '../shared/services';

import { checkIfServerIsDown } from './AccountActions/Helper';
import { setAlphaPropertiesDataErrors } from './AlphasPropertiesAction';
import { sessionExpire } from './AuthenticationActions';
import { addFlashMessages } from './FlashMessagesActions';
import { invalidRequest, retryAfterRequest } from './GenericActions';
import { getPerformanceEndpoint, getScoringCompetition } from './actionHelpers';
import { AlphaTableType } from '../types';

export const {
  responseAlphasSubmissionSuccess,
  responseAlphasSuccess,
  setAlphaDetail,
  setAlphaProperty,
  setAlphaTestPeriodShown,
  setAlphaDetailAsync,
  setDetailTab,
  setSelectedColumnsAlphas,
  resetSelectedColumnsAlphas,
  responseAlphasOptions,
  requestAlphas,
  responseAlphasFailure,
  removeDetail,
  requestUpdateAlphaProperties,
  responseUpdateAlphaProperties,
  responseUpdateAlphaPropertiesFailure,
  requestUpdateAlpha,
  responseUpdateAlpha,
  responseUpdateAlphaFailure,
  requestAlphasOptions,
  responseAlphasOptionsFailure,
  requestAlphasSubmission,
  responseAlphasSubmissionFailure,
  hasEnteredAlphaPage,
  changePath,
  mergeCorrelationToAlphas,
  introAlphasState,
  updateActiveButtonChart,
  updateHideButtonChart,
  setSingleAlphaDetail,
  requestSuperAlphaInfo,
  responseSuperAlphaInfoFailure,
  responseSuperAlphaInfoSuccess,
} = createActions(
  {
    RESPONSE_ALPHAS_SUBMISSION_SUCCESS: json => json,
    RESPONSE_ALPHAS_SUCCESS: data => data,
    SET_ALPHA_DETAIL: alphaIndex => alphaIndex,
    SET_ALPHA_PROPERTY: (name: string, value: any) => ({ name, value }),
    SET_ALPHA_TEST_PERIOD_SHOWN: (alphaDetailPath: string, isTestPeriodShown: boolean) => ({ alphaDetailPath, isTestPeriodShown }),
    SET_ALPHA_DETAIL_ASYNC: data => data,
    SET_DETAIL_TAB: tabIndex => tabIndex,
    SET_SELECTED_COLUMNS_ALPHAS: (selectedColumns: AlphaColumn) => ({ selectedColumns }),
    RESPONSE_ALPHAS_OPTIONS: options => options,
  },
  'REQUEST_ALPHAS',
  'RESPONSE_ALPHAS_FAILURE',
  'REMOVE_DETAIL',
  'RESET_SELECTED_COLUMNS_ALPHAS',
  'REQUEST_UPDATE_ALPHA_PROPERTIES',
  'RESPONSE_UPDATE_ALPHA_PROPERTIES',
  'RESPONSE_UPDATE_ALPHA_PROPERTIES_FAILURE',
  'REQUEST_UPDATE_ALPHA',
  'RESPONSE_UPDATE_ALPHA',
  'RESPONSE_UPDATE_ALPHA_FAILURE',
  'REQUEST_ALPHAS_OPTIONS',
  'RESPONSE_ALPHAS_OPTIONS_FAILURE',
  'REQUEST_ALPHAS_SUBMISSION',
  'RESPONSE_ALPHAS_SUBMISSION_FAILURE',
  'HAS_ENTERED_ALPHA_PAGE',
  'CHANGE_PATH',
  'MERGE_CORRELATION_TO_ALPHAS',
  'INTRO_ALPHAS_STATE',
  'UPDATE_ACTIVE_BUTTON_CHART',
  'UPDATE_HIDE_BUTTON_CHART',
  'SET_SINGLE_ALPHA_DETAIL',
  'REQUEST_SUPER_ALPHA_INFO',
  'RESPONSE_SUPER_ALPHA_INFO_FAILURE',
  'RESPONSE_SUPER_ALPHA_INFO_SUCCESS'
);

export const {
  responseAlphasPerformanceComparisonSuccess,
  responseAlphasCorrelationProdSuccess,
  responseAlphasCorrelationSuccess,
  responseAlphasCorrelationSuccessPowerPool,
  responseAlphasCheck,
  requestAlphasCheck,
  responseAlphasCheckFailure,
  requestAlphasCorrelation,
  requestAlphasCorrelationPowerPool,
  responseAlphasCorrelationFailure,
  responseAlphasCorrelationFailurePowerPool,
  requestAlphasCorrelationProd,
  responseAlphasCorrelationProdFailure,
  requestAlphasPerformanceComparison,
  responseAlphasPerformanceComparisonFailure,
} = createActions(
  {
    RESPONSE_ALPHAS_PERFORMANCE_COMPARISON_SUCCESS: (alphaId: string, json: any, date: any) => ({ alphaId, json, date }),
    RESPONSE_ALPHAS_CORRELATION_PROD_SUCCESS: (alphaId: string, json: any, date: any) => ({ alphaId, json, date }),
    RESPONSE_ALPHAS_CORRELATION_SUCCESS: (alphaId: string, json: CorrelationSelfType, date: any) => ({ alphaId, json, date }),
    RESPONSE_ALPHAS_CORRELATION_SUCCESS_POWER_POOL: (alphaId: string, json: CorrelationSelfType, date: any) => ({ alphaId, json, date }),
    RESPONSE_ALPHAS_CHECK: (alphaId: string, json: any) => ({ alphaId, json }),
    RESPONSE_ALPHAS_CORRELATION_FAILURE: (statusCode?: number) => ({ statusCode }),
    RESPONSE_ALPHAS_CORRELATION_FAILURE_POWER_POOL: (statusCode?: number) => ({ statusCode }),
    RESPONSE_ALPHAS_CORRELATION_PROD_FAILURE: (statusCode?: number) => ({ statusCode }),
  },
  'REQUEST_ALPHAS_CHECK',
  'RESPONSE_ALPHAS_CHECK_FAILURE',
  'REQUEST_ALPHAS_CORRELATION',
  'REQUEST_ALPHAS_CORRELATION_POWER_POOL',
  'REQUEST_ALPHAS_CORRELATION_PROD',
  'REQUEST_ALPHAS_PERFORMANCE_COMPARISON',
  'RESPONSE_ALPHAS_PERFORMANCE_COMPARISON_FAILURE'
);

export const {
  responseAlphasCountSuccess,
  responseRecordsSetChartSuccess,
  responseRecordsSetSummarySuccess,
  responseRecordsSetSuccess,
  requestRecordsSet,
  responseRecordsSetFailure,
  requestRecordsSetChart,
  responseRecordsSetChartFailure,
  requestRecordsSetSummary,
  responseRecordsSetSummaryFailure,
  changeTypeMenu,
  requestAlphasCount,
  responseAlphasCountFailure,
  clearAlphas,
  requestAlphasProdCount,
  responseAlphasProdCountSuccess,
  responseAlphasProdCountFailure,
  cleanRecordSetSummary,
  cleanRecordSet,
  cleanRecordSetChart,
  changeRecordSetName,
  changeChartType,
  requestSuperAlphas,
  responseSuperAlphasFailure,
  responseSuperAlphasSuccess,
} = createActions(
  {
    RESPONSE_ALPHAS_COUNT_SUCCESS: (results: any) => ({ results }),
    RESPONSE_RECORDS_SET_CHART_SUCCESS: (json: any, alphaId: string) => ({ json, alphaId }),
    RESPONSE_RECORDS_SET_SUMMARY_SUCCESS: (json: any, alphaId: string) => ({ json, alphaId }),
    RESPONSE_RECORDS_SET_SUCCESS: (json: any, alphaId: string) => ({ json, alphaId }),
  },
  'REQUEST_RECORDS_SET',
  'RESPONSE_RECORDS_SET_FAILURE',
  'REQUEST_RECORDS_SET_CHART',
  'RESPONSE_RECORDS_SET_CHART_FAILURE',
  'REQUEST_RECORDS_SET_SUMMARY',
  'RESPONSE_RECORDS_SET_SUMMARY_FAILURE',
  'CHANGE_TYPE_MENU',
  'REQUEST_ALPHAS_COUNT',
  'RESPONSE_ALPHAS_COUNT_FAILURE',
  'CLEAR_ALPHAS',
  'REQUEST_ALPHAS_PROD_COUNT',
  'RESPONSE_ALPHAS_PROD_COUNT_SUCCESS',
  'RESPONSE_ALPHAS_PROD_COUNT_FAILURE',
  'CLEAN_RECORD_SET_SUMMARY',
  'CLEAN_RECORD_SET',
  'CLEAN_RECORD_SET_CHART',
  'CHANGE_RECORD_SET_NAME',
  'CHANGE_CHART_TYPE',
  'REQUEST_SUPER_ALPHAS',
  'RESPONSE_SUPER_ALPHAS_FAILURE',
  'RESPONSE_SUPER_ALPHAS_SUCCESS'
);

export const setPath =
  (path: string) =>
  async (dispatch: any): Promise<object> =>
    new Promise(async (resolve, _) => {
      const res = dispatch(changePath(path));
      resolve(res);
    });

const changeToPermyriadOrPercent = (key: string, val: any) => {
  const value: RegExpExecArray | null = /[0-9\.,]+/.exec(val);

  if (value !== null) {
    const filterOperator = val.replace(/[0-9\.,]+/, '');
    let newValue: number = parseFloat(value[0]);
    newValue = key === 'is.margin' ? newValue / 10000 : newValue / 100;

    return `${filterOperator}${newValue}`;
  } else {
    return val;
  }
};

const divideByMillion = (val: any) => {
  const value: RegExpExecArray | null = /[0-9\.,]+/.exec(val);

  if (value !== null) {
    const filterOperator = val.replace(/[0-9\.,]+/, '');
    let newValue: number = parseFloat(value[0]);
    newValue = newValue / 1000000;

    return `${filterOperator}${newValue}`;
  } else {
    return val;
  }
};

function escapeChars(str: string) {
  const changeChars: any = { '#': '%23', '+': '%2B', '&': '%26', '=': '%3D', ',': '%1F' };

  return str.replace(/#|\.|\+|\&|=|,/g, (match: string) => (changeChars[match] ? changeChars[match] : match));
}

function filterQuery(filters: any) {
  if (!filters) {
    return '';
  }

  const formatKeys: any = PercentAndPermyriadFilters;
  const rangeFilters: any = AlphaRangeFilters;
  const millionFilters: any = MillionFilters;
  const regexFilter = new RegExp(`(${allColumnsParents.join('|')})\\.`);
  const regexAllFilter = new RegExp(`^(${allColumnsParents.join('|')})\\.`);

  return Object.keys(filters)
    .map(key => {
      let value = filters[key];

      if (key === 'alphaId') {
        return `id=${escapeChars(value)}`;
      }

      if (key === 'name') {
        return `${key}${escapeChars(value)}`;
      }

      if (key === 'tags') {
        return `tag${escapeChars(value)}`;
      }

      if (key === 'stage=') {
        return `${key}${escapeChars(value)}`;
      }

      if (key === 'dateCreated>') {
        key = `${key}=`;
        value = moment.tz(value.split('T')[0], 'America/New_York').format();
      } else if (key === 'dateCreated<') {
        value = moment.tz(value.split('T')[0], 'America/New_York').add(1, 'day').format();
      }

      if (!value) {
        return '';
      }
      if (/&+/.exec(value)) {
        let rangeValues: string = '';
        value = value.replace(',', '.');
        value.split('&').forEach((val: string) => {
          val = val.replace(',', '.');
          let trimedValue = val.trim();
          if (formatKeys.includes(key)) {
            trimedValue = changeToPermyriadOrPercent(key, trimedValue);
          }
          if (millionFilters.includes(key)) {
            trimedValue = divideByMillion(trimedValue);
          }
          if (regexAllFilter.exec(key)) {
            const filter = key.replace(regexFilter, '');
            if (rangeFilters.includes(filter) && /^(<|>|=|!|>=)/.exec(trimedValue) === null) {
              trimedValue = `=${trimedValue}`;
            }
          }
          rangeValues = rangeValues.concat(`${key}${trimedValue}&`);
        });

        return rangeValues.substring(0, rangeValues.length - 1);
      } else {
        if (formatKeys.includes(key)) {
          value = value.replace(',', '.');
          value = changeToPermyriadOrPercent(key, value);
        }
        if (millionFilters.includes(key)) {
          value = value.replace(',', '.');
          value = divideByMillion(value);
        }

        if (regexAllFilter.exec(key)) {
          value = value.replace(',', '.');
          const filter = key.replace(regexFilter, '');
          if (rangeFilters.includes(filter) && /^(<|>|=|!)/.exec(value) === null) {
            value = `=${value}`;
          }
        } else if (rangeFilters.includes(key) && /^(<|>|=|!)/.exec(value) === null) {
          // Handle range filters without parents (like osmosisPoints)
          value = value.replace(',', '.');
          value = `=${value}`;
        }

        if (key === 'competitions') {
          return `competition${value}`;
        }

        return `${key}${value}`;
      }
    })
    .join('&');
}

const getSuperAlphas =
  (id: string) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    if (state.getIn(['alphas', 'alphaDetail', 'fetchingAlphaFilters']) && state.getIn(['alphas', 'alphaDetail', 'superController'])) {
      state.getIn(['alphas', 'alphaDetail', 'superController']).abort();
    }
    const AbortController = window.AbortController;
    const controller = new AbortController();

    dispatch(requestSuperAlphas(controller));

    return globalFetch(
      `${ENDPOINTS_ALPHAS.alpha}/${id}/alphas?limit=5`,
      {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      },
      dispatch,
      () => dispatch(responseSuperAlphasFailure()),
      { endpointVersion: 'version=4.0' }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_ALPHAS_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseSuperAlphasFailure());
        } else if (Array.isArray(json) && json.length !== 0) {
          if (json[0] === 'Invalid query') {
            dispatch(addFlashMessages([{ msg: 'alphas.invalid.filters', defaultMessage: localeData.en['alphas.invalid.filters'] }]));
            logErrorService('Some filter is invalid', 'Some filter is invalid');

            return dispatch(responseSuperAlphasFailure());
          } else {
            dispatch(addFlashMessages(json, { context: 'generic', intl: false }));
            logErrorService(json, json);

            return dispatch(responseSuperAlphasFailure());
          }
        } else if (!t.exact(AlphasListingData).decode(json).isRight()) {
          webSimApiErrorFlashMessages(dispatch);
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, AlphasListingData);

          return dispatch(responseSuperAlphasFailure());
        } else {
          return dispatch(responseSuperAlphasSuccess(json));
        }
      })
      .catch((err: any) => {
        if (err.name !== 'AbortError') {
          defaultErrorMessage(dispatch);

          return dispatch(responseSuperAlphasFailure());
        } else {
          return dispatch(responseSuperAlphasFailure());
        }
      });
  };

export const setAlphaDetailAndGetInfo = (id: string) => async (dispatch: any, getState: any) => {
  const state = getState();
  const currentAlpha = state.getIn(['alphas', 'myAlphas', 'results']).find((alpha: any) => alpha.get('id') === id);
  const type = currentAlpha && currentAlpha.get('type');
  await dispatch(setAlphaDetail(id));
  if (type === 'SUPER') {
    dispatch(getSuperAlphas(id));
  }
};

export function getMyAlphas(endpoint: string, limit: number, offset: number, filters: any, hiddenFilters?: any) {
  return async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    if (state.getIn(['alphas', 'fetchingData']) && state.getIn(['alphas', 'controller'])) {
      state.getIn(['alphas', 'controller']).abort();
    }
    const AbortController = window.AbortController;
    const controller = new AbortController();
    if (endpoint === undefined || limit === undefined || offset === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestAlphas(controller));
    if (filters.hidden === undefined) {
      filters.hidden = '=false';
    }
    const callbackStatus400 = (response: any) =>
      response
        .json()
        .then((json: any) => {
          if (json) {
            dispatch(addFlashMessages(json, { context: 'generic', intl: false }));
          }

          return dispatch(responseAlphasFailure());
        })
        .catch((e: any) => {
          defaultErrorMessage(dispatch, 'alpha-details');
          logErrorService(e, e);

          return dispatch(responseAlphasFailure());
        });
    const newEndpoint =
      hiddenFilters && Object.keys(hiddenFilters).length > 0
        ? `${endpoint}?limit=${limit}&offset=${offset}&${filterQuery(filters)}&${filterQuery(hiddenFilters)}`
        : `${endpoint}/alphas?limit=${limit}&offset=${offset}&${filterQuery(filters)}`;

    return globalFetch(
      newEndpoint,
      {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      },
      dispatch,
      () => dispatch(responseAlphasFailure()),
      {
        hasAbortError: true,
        callbackStatus400,
        callbackStatus429: () =>
          dispatch(
            addFlashMessages(
              [
                {
                  msg: 'alphas.list.too.many.requests',
                  defaultMessage: localeData.en['alphas.list.too.many.requests'],
                },
              ],
              { context: 'generic' }
            )
          ),
        endpointVersion: 'version=4.0',
      }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_ALPHAS_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseAlphasFailure());
        } else if (Array.isArray(json) && json.length !== 0) {
          if (json[0] === 'Invalid query') {
            dispatch(addFlashMessages([{ msg: 'alphas.invalid.filters', defaultMessage: localeData.en['alphas.invalid.filters'] }]));
            logErrorService('Some filter is invalid', 'Some filter is invalid');

            return dispatch(responseAlphasFailure());
          } else {
            dispatch(addFlashMessages(json, { context: 'generic', intl: false }));
            logErrorService(json, json);

            return dispatch(responseAlphasFailure());
          }
        } else if (!t.exact(AlphasListingData).decode(json).isRight()) {
          webSimApiErrorFlashMessages(dispatch);
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, AlphasListingData);

          return dispatch(responseAlphasFailure());
        } else {
          return dispatch(responseAlphasSuccess(json));
        }
      })
      .catch((e: any) => {
        logErrorService(e, e);

        return dispatch(responseAlphasFailure());
      });
  };
}

export const normalize400AlphaPropertiesErrorData = (data: any) => {
  const result: any = {};
  if (Array.isArray(data.description)) {
    result.error = data.description;
  } else if (Array.isArray(data.tags)) {
    result.errortags = data.tags;
  } else if (Array.isArray(data.osmosisPoints)) {
    result.errorosmosisPoints = data.osmosisPoints;
  } else if (Array.isArray(data)) {
    result.error = data;
  } else {
    Object.keys(data).forEach((value: string) => {
      result[value] = normalize400AlphaPropertiesErrorData(data[value]);
    });
  }

  return result;
};

export function updateAlphaProperties(alphaId: string, data: any) {
  return async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    if (alphaId === undefined) {
      return dispatch(invalidRequest());
    }
    let payload = data;
    if (data === undefined) {
      const color = state.getIn(['alphaProperties', 'color']);
      const name = state.getIn(['alphaProperties', 'name']);
      const tags = state.getIn(['alphaProperties', 'tags']);
      const category = state.getIn(['alphaProperties', 'category']);
      const descriptionRegular = state.getIn(['alphaProperties', 'regular', 'description']);
      const descriptionSelection = state.getIn(['alphaProperties', 'selection', 'description']);
      const descriptionCombo = state.getIn(['alphaProperties', 'combo', 'description']);
      payload = {
        color: color !== 'NONE' && color.trim().length !== 0 ? color : null,
        name: name !== '' ? name : null,
        tags: tags.toJS(),
        category: category !== '' ? category : null,
        regular: descriptionRegular !== '' ? { description: descriptionRegular } : undefined,
        combo: descriptionCombo !== '' ? { description: descriptionCombo } : undefined,
        selection: descriptionSelection !== '' ? { description: descriptionSelection } : undefined,
      };
    }
    dispatch(requestUpdateAlphaProperties());

    const callbackStatus400 = (response: any) =>
      response
        .json()
        .then((json: any) => {
          if (json) {
            dispatch(setAlphaPropertiesDataErrors({ ...normalize400AlphaPropertiesErrorData(json), showErrors: true }));
          }

          return dispatch(responseUpdateAlphaPropertiesFailure());
        })
        .catch((e: any) => {
          defaultErrorMessage(dispatch, 'alpha-details');
          logErrorService(e, e);

          return dispatch(responseUpdateAlphaPropertiesFailure());
        });

    return globalFetch(
      `${ENDPOINTS_ALPHAS.alpha}/${alphaId}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(payload),
      },
      dispatch,
      () => dispatch(responseUpdateAlphaPropertiesFailure()),
      {
        callbackStatus400,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_UPDATE_ALPHA_PROPERTIES_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseUpdateAlphaPropertiesFailure());
        } else {
          dispatch(setAlphaDetailAsync(json));

          return dispatch(responseUpdateAlpha(json));
        }
      })
      .catch(e => {
        defaultErrorMessage(dispatch, 'alpha-details');
        logErrorService(e, e);

        return dispatch(responseUpdateAlphaPropertiesFailure());
      });
  };
}

export function updateAlpha(alphaId: string, data: any) {
  return async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();

    if (state.getIn(['alphas', 'fetchingProperties']) === true || alphaId === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestUpdateAlpha());

    return globalFetch(
      `${ENDPOINTS_ALPHAS.alpha}/${alphaId}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(data),
      },
      dispatch,
      () => dispatch(responseUpdateAlphaFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_UPDATE_ALPHA_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseUpdateAlphaFailure());
        }

        return dispatch(responseUpdateAlpha(json));
      })
      .catch(() => {
        defaultErrorMessage(dispatch);
        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseUpdateAlphaFailure());
      });
  };
}

export function updateBulkAlpha(data: any) {
  return async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();

    if (state.getIn(['alphas', 'fetchingProperties']) === true) {
      return dispatch(invalidRequest());
    }

    dispatch(requestUpdateAlpha());

    return globalFetch(
      `${ENDPOINTS_ALPHAS.alpha}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(data),
      },
      dispatch,
      () => dispatch(responseUpdateAlphaFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_UPDATE_ALPHA_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseUpdateAlphaFailure());
        } else {
          return dispatch(responseUpdateAlpha(json));
        }
      })
      .catch((e: any) => {
        defaultErrorMessage(dispatch);
        logErrorService(e, e);

        return dispatch(responseUpdateAlphaFailure());
      });
  };
}

export function fetchAlphasOptions(id: string) {
  return async (dispatch: any): Promise<object> => {
    if (id === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestAlphasOptions());

    return globalFetch(
      `${ENDPOINTS_USER.user}/${id}/alphas`,
      {
        method: 'OPTIONS',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseAlphasOptionsFailure()),
      { endpointVersion: 'version=4.0' }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_ALPHAS_OPTIONS_FAILURE')) {
          return json;
        } else if (json && json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseAlphasOptionsFailure());
        } else if (t.exact(AlphasOptions).decode(json).isRight()) {
          return dispatch(responseAlphasOptions(json.actions.GET));
        } else {
          logErrorService(json, json);
          webSimApiErrorFlashMessages(dispatch);

          return dispatch(responseAlphasOptionsFailure());
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);

        return dispatch(responseAlphasOptionsFailure());
      });
  };
}

export function getAlpha(alpha: string, loading: boolean = true) {
  return async (dispatch: any): Promise<object> => {
    if (alpha === undefined) {
      return dispatch(invalidRequest());
    }

    if (loading) {
      dispatch(requestAlphas());
    }

    return globalFetch(
      `${ENDPOINTS_ALPHAS.alpha}/${alpha}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseAlphasFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_ALPHAS_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseAlphasFailure());
        } else if (!t.exact(AlphasDataResults).decode(json).isRight()) {
          webSimApiErrorFlashMessages(dispatch);
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, AlphasDataResults);

          return dispatch(responseAlphasFailure());
        } else {
          if (json.type === 'SUPER') dispatch(getSuperAlphas(json.id));
          return dispatch(setAlphaDetailAsync(json));
        }
      })
      .catch(() => {
        defaultErrorMessage(dispatch);
        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseAlphasFailure());
      });
  };
}

export function getSuperAlpha(alpha: string) {
  return async (dispatch: any): Promise<object> => {
    if (alpha === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestSuperAlphaInfo());

    return globalFetch(
      `${ENDPOINTS_ALPHAS.alpha}/${alpha}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseSuperAlphaInfoFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_SUPER_ALPHA_INFO_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseSuperAlphaInfoFailure());
        } else if (!t.exact(AlphasDataResults).decode(json).isRight()) {
          webSimApiErrorFlashMessages(dispatch);
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, AlphasDataResults);

          return dispatch(responseSuperAlphaInfoFailure());
        } else {
          return dispatch(responseSuperAlphaInfoSuccess(json));
        }
      })
      .catch(() => {
        defaultErrorMessage(dispatch);
        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseSuperAlphaInfoFailure());
      });
  };
}

const submitAlphaCheckError = async (result: any, dispatch: any, alpha: string): Promise<object> => {
  if (result.status === 'success') {
    dispatch(responseAlphasSubmissionSuccess(result));
    dispatch(
      addFlashMessages([{ msg: 'dataAction.alphaSucess', defaultMessage: localeData.en['dataAction.alphaSucess'] }], {
        location: 'alpha-details',
        type: 'info',
      })
    );
    const simMessageDiv: any = document.getElementsByClassName('flash--tags-success')[0];
    if (simMessageDiv) {
      simMessageDiv.scrollIntoView(true);
    }
    dispatch(responseAlphasCheck(alpha, result.json ? result.json.is.checks : undefined));

    return dispatch(getAlpha(alpha));
  } else if (result.status === 'failed') {
    if (result.json && result.json.is.checks) {
      dispatch(responseAlphasCheck(alpha, result.json.is.checks));
    }
    if (result.json && result.json.is.selfCorrelation) {
      dispatch(responseAlphasCorrelationSuccess(alpha, result.json.is.selfCorrelation, moment()));
    }
    const failedChecks = result.json ? result.json.is.checks.filter((metric: any) => metric.result === 'FAIL') : [];
    if (failedChecks.length > 0) {
      dispatch(
        addFlashMessages(
          [
            {
              msg: 'dataAction.failedChecks',
              defaultMessage: localeData.en['dataAction.failedChecks'],
              intlValues: { count: failedChecks.length },
            },
          ],
          {
            location: 'alpha-details',
            context: 'generic',
          }
        )
      );
    }
    const simMessageDiv: any = document.getElementsByClassName('flash--check-alphas')[0];
    if (simMessageDiv) {
      simMessageDiv.scrollIntoView(true);
    }

    return dispatch(responseAlphasSubmissionFailure());
  }

  return dispatch(responseAlphasSubmissionFailure());
};

export function submitAlpha(alpha: string, method: string = 'POST') {
  return async (dispatch: any): Promise<object> => {
    if (alpha === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestAlphasSubmission());

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alpha}/submit`, {
      method: method,
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          const timeOut = response.headers.get('Retry-After');
          if (timeOut !== null) {
            setTimeout(() => dispatch(submitAlpha(alpha, (method = 'GET'))), timeOut * 1000);

            return dispatch(retryAfterRequest());
          } else {
            return response
              .json()
              .then(async (json: any): Promise<object> => submitAlphaCheckError({ status: 'success', json }, dispatch, alpha));
          }
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);
          dispatch(sessionExpire());

          return dispatch(responseAlphasSubmissionFailure());
        } else if (response.status === 429) {
          dispatch(
            addFlashMessages(
              [
                {
                  msg: 'alphas.submit.too.many.requests',
                  defaultMessage: localeData.en['alphas.submit.too.many.requests'],
                },
              ],
              {
                location: 'alpha-details',
                context: 'generic',
                links: [
                  {
                    urls: [`${FAQ_URL}/articles/18423410021783--BRAIN-TIPS-Troubleshooting-common-error-messages`],
                    keys: ['alphas.submit.too.many.requests.link'],
                    defaultMsgkeys: [localeData.en['alphas.submit.too.many.requests.link']],
                  },
                ],
              }
            )
          );

          return dispatch(responseAlphasSubmissionFailure());
        } else if (response.status === 500) {
          logErrorService(response.statusText, response.status);

          return dispatch(responseAlphasSubmissionFailure());
        } else {
          return response
            .json()
            .then(async (json: any): Promise<object> => submitAlphaCheckError({ status: 'failed', json: json }, dispatch, alpha));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);

        return dispatch(responseAlphasSubmissionFailure());
      });
  };
}

export function alphasCheck(alphaId: string) {
  return async (dispatch: any): Promise<object> => {
    if (alphaId === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestAlphasCheck());

    return globalFetch(
      `${ENDPOINTS_ALPHAS.alpha}/${alphaId}/check`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseAlphasCheckFailure()),
      {
        callbackAction: () => dispatch(alphasCheck(alphaId)),
        answerEmpty: false,
        retryEmpty: true,
        callbackStatus429: () =>
          dispatch(
            addFlashMessages(
              [
                {
                  msg: 'alphas.check.too.many.requests',
                  defaultMessage: localeData.en['alphas.check.too.many.requests'],
                },
              ],
              {
                location: 'alpha-details',
                context: 'generic',
              }
            )
          ),
      }
    )
      .then(async (json: any): Promise<object> => {
        if (json) {
          if (isAction(json, 'RESPONSE_ALPHAS_CHECK_FAILURE')) {
            return json;
          } else if (t.exact(CheckSubmissionValidation).decode(json).isRight()) {
            dispatch(responseAlphasCorrelationSuccess(alphaId, json.is.checks, moment()));
            const failedChecks = json.is.checks.filter((metric: any) => metric.result === 'FAIL');
            if (failedChecks.length === 0) {
              return dispatch(responseAlphasCheck(alphaId, json.is.checks));
            }
            dispatch(
              addFlashMessages(
                [
                  {
                    msg: 'dataAction.failedChecks',
                    defaultMessage: localeData.en['dataAction.failedChecks'],
                    intlValues: { count: failedChecks.length },
                  },
                ],
                {
                  location: 'alpha-details',
                  context: 'generic',
                }
              )
            );
            const simMessageDiv: any = document.getElementsByClassName('flash--check-alphas')[0];
            if (simMessageDiv) {
              simMessageDiv.scrollIntoView(true);
            }

            return dispatch(responseAlphasCheck(alphaId, json.is.checks));
          } else {
            // It will report to sentry the io-ts validation errors
            ioTsValidationError(json, CheckSubmissionValidation);

            return dispatch(responseAlphasCheckFailure());
          }
        }

        return dispatch(responseAlphasCheckFailure());
      })
      .catch((err: any) => {
        logErrorService(err, err);

        return dispatch(responseAlphasCheckFailure());
      });
  };
}

const errorMessagesStatusCode: any = [401, 410, 412, 429, 503];

export const getSelfAlphasCorrelations = (alphaId: string, showError: boolean = true) =>
  getAlphasCorrelationsResult({
    alphaId,
    request: requestAlphasCorrelation,
    responseFailure: responseAlphasCorrelationFailure,
    responseSuccess: responseAlphasCorrelationSuccess,
    failureType: 'RESPONSE_ALPHAS_CORRELATION_FAILURE',
    apiPath: 'self',
    showError,
  });

export const getAlphasCorrelationsPowerPool = (alphaId: string, showError: boolean = true) =>
  getAlphasCorrelationsResult({
    alphaId,
    request: requestAlphasCorrelationPowerPool,
    responseFailure: responseAlphasCorrelationFailurePowerPool,
    responseSuccess: responseAlphasCorrelationSuccessPowerPool,
    failureType: 'RESPONSE_ALPHAS_CORRELATION_FAILURE_POWER_POOL',
    apiPath: 'power-pool',
    showError,
  });

type AlphaCorrelationsResultInput = {
  alphaId: string;
  request: (...args: any[]) => Action<any>;
  responseFailure: (...args: any[]) => Action<any>;
  responseSuccess: (...args: any[]) => Action<any>;
  failureType: string;
  apiPath: string;
  showError?: boolean;
};

export const getAlphasCorrelationsResult =
  ({ alphaId, request, responseFailure, responseSuccess, failureType, apiPath, showError = true }: AlphaCorrelationsResultInput) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    if (alphaId === undefined || state.getIn(['alphas', 'alphaDetail', 'id']) !== alphaId) {
      return dispatch(invalidRequest());
    }
    dispatch(request());

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/correlations/${apiPath}`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        const timeOut = response.headers.get('Retry-After');
        if (timeOut) {
          setTimeout(
            () => dispatch(getAlphasCorrelationsResult({ alphaId, request, responseFailure, responseSuccess, failureType, apiPath })),
            parseFloat(timeOut) * 1000
          );

          return dispatch(retryAfterRequest());
        } else if (errorMessagesStatusCode.includes(response.status)) {
          return specialCasesCorrelation({
            response,
            dispatch,
            context: 'generic',
            location: 'alpha-details',
            dispatchFailure: () => dispatch(responseFailure(response.status)),
          });
        } else if (response.status >= 500 && response.status <= 504) {
          if (!showError) {
            return dispatch(responseFailure());
          }
          return checkIfServerIsDown(dispatch, response, () => dispatch(responseFailure()), 'alpha-details');
        } else {
          return response.json();
        }
      })
      .then(async (json: any): Promise<object | undefined> => {
        if (isAction(json, failureType)) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseFailure());
        } else {
          if (t.exact(CorrelationSelf).decode(json).isRight()) {
            return dispatch(responseSuccess(alphaId, json, moment()));
          } else {
            logErrorService(json, json);
            webSimApiErrorFlashMessages(dispatch, 'generic', 'alpha-details');

            return dispatch(responseFailure());
          }
        }
      })
      .catch(() => {
        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);
        webSimApiErrorFlashMessages(dispatch, 'generic', 'alpha-details');

        return dispatch(responseAlphasCorrelationFailure());
      });
  };

export const getAlphasCorrelationsProd =
  (alphaId: string) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    if (alphaId === undefined || state.getIn(['alphas', 'alphaDetail', 'id']) !== alphaId) {
      return dispatch(invalidRequest());
    }
    dispatch(requestAlphasCorrelationProd());

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/correlations/prod`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        const timeOut = response.headers.get('Retry-After');
        if (timeOut) {
          setTimeout(() => dispatch(getAlphasCorrelationsProd(alphaId)), parseFloat(timeOut) * 1000);

          return dispatch(retryAfterRequest());
        } else if (errorMessagesStatusCode.includes(response.status)) {
          return specialCasesCorrelation({
            response,
            dispatch,
            context: 'generic',
            location: 'alpha-details',
            dispatchFailure: () => dispatch(responseAlphasCorrelationProdFailure(response.status)),
          });
        } else if (response.status >= 500 && response.status <= 504) {
          return checkIfServerIsDown(dispatch, response, () => dispatch(responseAlphasCorrelationProdFailure()), 'alpha-details');
        } else {
          return response.json();
        }
      })
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_ALPHAS_CORRELATION_PROD_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseAlphasCorrelationProdFailure());
        } else {
          if (t.exact(CorrelationProd).decode(json).isRight()) {
            return dispatch(responseAlphasCorrelationProdSuccess(alphaId, json, moment()));
          } else {
            // It will be reported to sentry the io-ts validation errors
            ioTsValidationError(json, CorrelationProd);

            return dispatch(responseAlphasCorrelationProdFailure());
          }
        }
      })
      .catch(() => {
        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);
        webSimApiErrorFlashMessages(dispatch, 'generic', 'alpha-details');

        return dispatch(responseAlphasCorrelationProdFailure());
      });
  };

export const getPerformanceComparison =
  (alphaId: string, path: string[]) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    const scoringCompetition = getScoringCompetition(state, state.getIn(path), alphaId);

    const teamId = state.getIn(['team', 'activeTeam', 'activeTeam', 'id']);
    if (alphaId === undefined || state.getIn(['alphas', 'alphaDetail', 'id']) !== alphaId) {
      return dispatch(invalidRequest());
    }
    dispatch(requestAlphasPerformanceComparison());

    return globalFetch(
      getPerformanceEndpoint({ alphaId: alphaId, teamId: teamId, scoringCompetition: scoringCompetition }),
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseAlphasPerformanceComparisonFailure()),
      {
        callbackAction: () => dispatch(getPerformanceComparison(alphaId, path)),
        retryEmpty: true,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (json && json.type === 'RESPONSE_ALPHAS_PERFORMANCE_COMPARISON_FAILURE') {
          logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);
          webSimApiErrorFlashMessages(dispatch, 'generic', 'alpha-details');

          return json;
        } else if (isAction(json, '')) {
          return json;
        } else {
          return dispatch(responseAlphasPerformanceComparisonSuccess(alphaId, json, moment().utc().format()));
        }
      })
      .catch((error: any) => {
        logErrorService(error, error);
        webSimApiErrorFlashMessages(dispatch, 'generic', 'alpha-details');

        return dispatch(responseAlphasPerformanceComparisonFailure());
      });
  };

export const getRecordsSetChart =
  (alphaId: string, record: string) =>
  async (dispatch: any): Promise<object> => {
    if (alphaId === undefined || record === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestRecordsSetChart());

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/recordsets/${record}`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          const timeOut = response.headers.get('Retry-After');
          if (timeOut !== null) {
            setTimeout(() => dispatch(getRecordsSetChart(alphaId, record)), timeOut * 1000);

            return dispatch(retryAfterRequest());
          } else {
            return response.json();
          }
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);
          dispatch(sessionExpire());

          return dispatch(responseRecordsSetChartFailure());
        } else if (response.status === 404) {
          return dispatch(responseRecordsSetChartFailure());
        } else {
          dispatch(
            addFlashMessages([{ msg: 'api.error.brain.experiencing', defaultMessage: localeData.en['api.error.brain.experiencing'] }], {
              links: [
                {
                  urls: [SUPPORT_URL_PLACEHOLDER],
                  keys: ['api.error.brain.experiencing.link'],
                  defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
                },
              ],
              location: 'alpha-details',
            })
          );
          logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

          return dispatch(responseRecordsSetChartFailure());
        }
      })
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_RECORDS_SET_CHART_FAILURE')) {
          return json;
        }
        if (json.detail) {
          logErrorService(json.detail, json.detail);

          return dispatch(responseRecordsSetChartFailure());
        } else {
          if (t.exact(ChartData).decode(json).isRight()) {
            return dispatch(responseRecordsSetChartSuccess(json, alphaId));
          } else {
            // It will be reported to sentry the io-ts validation errors
            ioTsValidationError(json, ChartData);

            return dispatch(responseRecordsSetChartFailure());
          }
        }
      })
      .catch(() => {
        dispatch(
          addFlashMessages([{ msg: 'api.error.brain.experiencing', defaultMessage: localeData.en['api.error.brain.experiencing'] }], {
            links: [
              {
                urls: [SUPPORT_URL_PLACEHOLDER],
                keys: ['api.error.brain.experiencing.link'],
                defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
              },
            ],
            location: 'alpha-details',
          })
        );
        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseRecordsSetChartFailure());
      });
  };

export const getRecordsSetSummary =
  (alphaId: string, record: string) =>
  async (dispatch: any): Promise<object> => {
    if (alphaId === undefined || record === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestRecordsSetSummary());

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/recordsets/${record}`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          const timeOut = response.headers.get('Retry-After');
          if (timeOut !== null) {
            setTimeout(() => dispatch(getRecordsSetSummary(alphaId, record)), timeOut * 1000);

            return dispatch(retryAfterRequest());
          } else {
            return response.json();
          }
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);
          dispatch(sessionExpire());

          return dispatch(responseRecordsSetSummaryFailure());
        } else if (response.status === 404) {
          return dispatch(responseRecordsSetSummaryFailure());
        } else {
          dispatch(
            addFlashMessages([{ msg: 'api.error.brain.experiencing', defaultMessage: localeData.en['api.error.brain.experiencing'] }], {
              links: [
                {
                  urls: [SUPPORT_URL_PLACEHOLDER],
                  keys: ['api.error.brain.experiencing.link'],
                  defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
                },
              ],
              location: 'alpha-details',
            })
          );
          logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

          return dispatch(responseRecordsSetSummaryFailure());
        }
      })
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_RECORDS_SET_SUMMARY_FAILURE')) {
          return json;
        }
        if (json.detail) {
          logErrorService(json.detail, json.detail);

          return dispatch(responseRecordsSetSummaryFailure());
        } else {
          return dispatch(responseRecordsSetSummarySuccess(json, alphaId));
        }
      })
      .catch(e => {
        dispatch(
          addFlashMessages([{ msg: 'api.error.brain.experiencing', defaultMessage: localeData.en['api.error.brain.experiencing'] }], {
            links: [
              {
                urls: [SUPPORT_URL_PLACEHOLDER],
                keys: ['api.error.brain.experiencing.link'],
                defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
              },
            ],
            location: 'alpha-details',
          })
        );
        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseRecordsSetSummaryFailure());
      });
  };

export const getRecordsSet =
  (alphaId: string) =>
  async (dispatch: any): Promise<object> => {
    if (alphaId === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestRecordsSet());

    return globalFetch(
      `${ENDPOINTS_ALPHAS.alpha}/${alphaId}/recordsets`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
      },
      dispatch,
      () => dispatch(responseRecordsSetFailure()),
      {
        callbackAction: () => dispatch(getRecordsSet(alphaId)),
        contactSupportMessage: () =>
          dispatch(
            addFlashMessages([{ msg: 'api.error.brain.experiencing', defaultMessage: localeData.en['api.error.brain.experiencing'] }], {
              links: [
                {
                  urls: [SUPPORT_URL_PLACEHOLDER],
                  keys: ['api.error.brain.experiencing.link'],
                  defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
                },
              ],
              location: 'alpha-details',
            })
          ),
        answerEmpty: false,
        retryEmpty: true,
      }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_RECORDS_SET_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseRecordsSetFailure());
        } else {
          if (t.exact(RecordsSets).decode(json).isRight()) {
            return dispatch(responseRecordsSetSuccess(json, alphaId));
          } else {
            // It will be reported to sentry the io-ts validation errors
            ioTsValidationError(json, RecordsSets);

            return dispatch(responseRecordsSetFailure());
          }
        }
      })
      .catch(() => {
        dispatch(
          addFlashMessages([{ msg: 'api.error.brain.experiencing', defaultMessage: localeData.en['api.error.brain.experiencing'] }], {
            links: [
              {
                urls: [SUPPORT_URL_PLACEHOLDER],
                keys: ['api.error.brain.experiencing.link'],
                defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
              },
            ],
            location: 'alpha-details',
          })
        );
        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseRecordsSetFailure());
      });
  };

export const changeMenuTab = (id: AlphaTableType) => (dispatch: any) => {
  dispatch(changeTypeMenu(id));
};

export const getAlphasCount =
  () =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    const timeToLogout = state.getIn(['user', 'timeToLogout']);

    if (timeToLogout === null) {
      return dispatch(invalidRequest());
    }

    dispatch(requestAlphasCount());

    return globalFetch(
      `${ENDPOINTS_USER.user}/self/alphas/summary`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseAlphasCountFailure()),
      { endpointVersion: 'version=4.0' }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_ALPHAS_COUNT_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseAlphasCountFailure());
        } else {
          return dispatch(responseAlphasCountSuccess(json));
        }
      })
      .catch(() => {
        defaultErrorMessage(dispatch);
        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseAlphasCountFailure());
      });
  };
